import { BUILDING_SECTIONS } from "./buildingSections";

// Truncates (never rounds) to 2 decimal places — used wherever a BCI value is shown
// (BCI Calculation's footer, and the Building Circularity metric cards that mirror it).
export const truncateTo2Decimals = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  return (Math.trunc(Number(num) * 100) / 100).toFixed(2);
};

// Shearing Layers (Stewart Brand) mapped onto the existing WBLCA Uniformat subcategories
// (buildingSections.js) — every one of the 15 subcategories is assigned to exactly one
// layer here. Single source of truth shared by SCI Calculation and BCI Calculation, so
// both stay in sync automatically if this mapping ever changes.
export const SHEARING_LAYERS = [
  { name: "Structure Layer", subcategoryIds: ["foundations", "basement_construction", "superstructure", "stairs"] },
  { name: "Skin Layer", subcategoryIds: ["exterior_enclosure", "roofing"] },
  { name: "Services Layer", subcategoryIds: ["conveying", "plumbing", "hvac", "fire_protection", "electrical"] },
  { name: "Space Plan Layer", subcategoryIds: ["interior_construction", "interior_finishes"] },
  { name: "Stuff Layer", subcategoryIds: ["equipment", "furnishings"] },
];

// id -> display name lookup, e.g. "basement_construction" -> "Basement Construction"
export const SUBCATEGORY_NAME_BY_ID = BUILDING_SECTIONS.reduce((acc, section) => {
  section.subcategories.forEach((sub) => {
    acc[sub.id] = sub.name;
  });
  return acc;
}, {});

// Same CT/CA/Independency/GPE defaults and Fi/PCI formula as DesignAssessmentPanel.js.
const parseNumeric = (val, defaultVal) => {
  if (val === null || val === undefined) return defaultVal;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? defaultVal : parsed;
};

// One row per WBLCA subcategory (grouped by Shearing Layer), populated from the real
// materials matched to each subcategory via their process's building_section_subcategory:
//   mass   = Σ mass from the WBLCA Environmental Factor table for that subcategory
//   mciVal = mass-weighted average of the same MCI field ProjectMCICalculationTable shows
//   pciVal = mass-weighted average of DesignAssessmentPanel's own formula
//            (PCI = MCI × Fi, Fi from ct_factor/ca_factor/indep_factor/gpe_factor)
// sciTheoretical/sciPractical are computed PER SHEARING LAYER (not once across the whole
// set) and repeated on every row belonging to that layer:
//   sciTheoretical = Σ(MCI × Mass) / Σ(Mass)   — summed over that layer's rows only
//   sciPractical   = Σ(PCI × Mass) / Σ(Mass)   — summed over that layer's rows only
export function computeShearingLayerRows(combined_data, processes = []) {
  const materials = combined_data?.combined_circularities || [];

  const bySubcategory = {};
  materials.forEach((m) => {
    const process = processes.find((p) => String(p.id) === String(m.process_id));
    const subId = process?.building_section_subcategory;
    if (!subId) return;

    const mciVal = m.percentage_mci !== undefined
      ? Number(m.percentage_mci)
      : (m.MCI !== undefined
        ? Math.max(0, (Number(m.MCI) - 0.1) / 0.9)
        : (m.row_mci !== undefined ? Math.max(0, (Number(m.row_mci) - 0.1) / 0.9) : 0));
    const ct = parseNumeric(m.ct_factor, 0.8);
    const ca = parseNumeric(m.ca_factor, 0.8);
    const indep = parseNumeric(m.indep_factor, 0.4);
    const gpe = parseNumeric(m.gpe_factor, 0.4);
    const fi = 4 / ((1 / ct) + (1 / ca) + (1 / indep) + (1 / gpe));
    const pci = mciVal * fi;
    // _massIsNA (set in BuildingCircularity.js) means the WBLCA source had no real mass —
    // calculate_mass's quantity-derived fallback estimate must not count toward mass sums
    // or the mass-weighted MCI/PCI averages below, same as Material Composition's total.
    const massIsNA = !!m._massIsNA;
    const mass = massIsNA ? 0 : (m.mass || 0);

    if (!bySubcategory[subId]) bySubcategory[subId] = [];
    bySubcategory[subId].push({ mciVal, pci, mass, massIsNA });
  });

  return SHEARING_LAYERS.flatMap((layer) => {
    // All materials across every subcategory in this one layer, for the layer-level SCI.
    const layerMaterials = layer.subcategoryIds.flatMap((subId) => bySubcategory[subId] || []);
    const layerMass = layerMaterials.reduce((s, m) => s + m.mass, 0);
    const layerSciTheoretical = layerMass > 0
      ? layerMaterials.reduce((s, m) => s + m.mciVal * m.mass, 0) / layerMass
      : null;
    const layerSciPractical = layerMass > 0
      ? layerMaterials.reduce((s, m) => s + m.pci * m.mass, 0) / layerMass
      : null;

    return layer.subcategoryIds.map((subId) => {
      const subMaterials = bySubcategory[subId] || [];
      const subMass = subMaterials.reduce((s, m) => s + m.mass, 0);
      const mciVal = subMass > 0 ? subMaterials.reduce((s, m) => s + m.mciVal * m.mass, 0) / subMass : null;
      const pciVal = subMass > 0 ? subMaterials.reduce((s, m) => s + m.pci * m.mass, 0) / subMass : null;
      // Total mass from the WBLCA Environmental Factor table for this subcategory's
      // materials — null (not 0) when every material present has no real mass, so it
      // still displays as N/A rather than a misleading "0.00".
      const hasRealMass = subMaterials.some((m) => !m.massIsNA);
      const mass = hasRealMass ? subMass : null;

      return {
        id: subId,
        shearingLayer: layer.name,
        description: SUBCATEGORY_NAME_BY_ID[subId] || subId,
        mciVal,
        pciVal,
        mass,
        sciTheoretical: layerSciTheoretical,
        sciPractical: layerSciPractical,
      };
    });
  });
}

// Site Layer isn't part of the WBLCA Uniformat classification (buildingSections.js), so it
// has no real subcategory/materials — one static placeholder row, always first.
export const SITE_LAYER_ROW = {
  id: "site",
  shearingLayer: "Site Layer",
  description: "Not represented in current classification",
  sciTheoretical: null,
  sciPractical: null,
};

// Importance Weight per Shearing Layer (fixed reference values, not derived from data).
export const IMPORTANCE_WEIGHT_BY_LAYER = {
  "Site Layer": 0.1,
  "Structure Layer": 0.2,
  "Skin Layer": 0.7,
  "Services Layer": 0.8,
  "Space Plan Layer": 0.9,
  "Stuff Layer": 1.0,
};

// One row per unique Shearing Layer (Site + the 5 from computeShearingLayerRows, collapsed
// from 15 subcategory rows down to their 6 parent layers) — shared by BCI Calculation's
// grid and computeBciMetrics below, so both stay in sync.
export function computeBciLayerRows(combined_data, processes = []) {
  const sciRows = computeShearingLayerRows(combined_data, processes);
  const layerRows = [SITE_LAYER_ROW, ...sciRows].map((row) => ({
    ...row,
    importanceWeight: IMPORTANCE_WEIGHT_BY_LAYER[row.shearingLayer] ?? null,
  }));

  const uniqueLayers = [];
  const seenLayers = new Set();
  layerRows.forEach((row) => {
    if (!seenLayers.has(row.shearingLayer)) {
      seenLayers.add(row.shearingLayer);
      uniqueLayers.push(row);
    }
  });
  return uniqueLayers;
}

// BCI (theoretical/practical) — single whole-building values, computed "layer wise":
//   BCI (theoretical) = Σ(SCI theoretical × Weight) / Σ(Weight), across the 6 layers
//   BCI (practical)   = Σ(SCI practical × Weight) / Σ(Weight), across the 6 layers
// The denominator Σ(Weight) is always the FULL sum of all 6 layers' weights, even ones
// with no SCI value yet (e.g. Site Layer) — those layers just contribute 0 to the
// numerator, they still count against the total. Shared by BCI Calculation's footer and
// the Building Circularity metric cards, so both always show the same value.
export function computeBciMetrics(combined_data, processes = []) {
  const rows = computeBciLayerRows(combined_data, processes);
  const totalWeight = rows.reduce((s, l) => s + (l.importanceWeight || 0), 0);

  const validForTheoretical = rows.filter((l) => l.sciTheoretical !== null && l.sciTheoretical !== undefined && l.importanceWeight !== null);
  const bciTheoretical = totalWeight > 0
    ? validForTheoretical.reduce((s, l) => s + l.sciTheoretical * l.importanceWeight, 0) / totalWeight
    : 0;

  const validForPractical = rows.filter((l) => l.sciPractical !== null && l.sciPractical !== undefined && l.importanceWeight !== null);
  const bciPractical = totalWeight > 0
    ? validForPractical.reduce((s, l) => s + l.sciPractical * l.importanceWeight, 0) / totalWeight
    : 0;

  return { rows, bciTheoretical, bciPractical };
}
